/**
 * Writing editor component.
 *
 * Provides a distraction-free text editor with word count tracking, a
 * minimal Cut/Copy/Paste toolbar, and a lined-paper background. The
 * existing word-count pill, min/max status colors, and time-warning ring
 * are preserved unchanged in the footer.
 *
 * The optional `headerRightSlot` (typically the Task overview button) is
 * rendered inline at the right edge of the toolbar so the editor stays
 * visually compact and matches the original layout (no separate header
 * row, no extra word-limit progress bar).
 *
 * @module WritingEditor
 */

import { useState, useCallback, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { Copy, Scissors, Clipboard, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { SimulationTaskProgress } from "@/components/exam/shared";
import { cn } from "@/lib/utils";

interface WritingEditorProps {
  readonly content: string;
  readonly onChange: (content: string) => void;
  readonly minWords?: number;
  readonly isTimeWarning?: boolean;
  readonly progressCurrent: number;
  readonly progressTotal: number;
  /** Optional element rendered at the right edge of the toolbar (e.g. Task overview). */
  readonly headerRightSlot?: ReactNode;
}

export function WritingEditor({
  content,
  onChange,
  minWords = 150,
  isTimeWarning = false,
  progressCurrent,
  progressTotal,
  headerRightSlot,
}: WritingEditorProps) {
  const { t } = useTranslation();
  const [copiedAction, setCopiedAction] = useState<string | null>(null);

  const wordCount = content.trim() ? content.trim().split(/\s+/).length : 0;

  // No upper word limit is enforced visually: once the writer reaches the
  // target minimum, the pill stays in its "good" state regardless of how
  // many words they keep adding. `maxWords` is intentionally ignored here.
  const status: "under" | "good" = wordCount < minWords ? "under" : "good";

  const handleCopy = useCallback(async () => {
    const selection = window.getSelection()?.toString() || content;
    await navigator.clipboard.writeText(selection);
    setCopiedAction("copy");
    setTimeout(() => setCopiedAction(null), 1500);
  }, [content]);

  const handleCut = useCallback(async () => {
    const selection = window.getSelection()?.toString();
    if (selection) {
      await navigator.clipboard.writeText(selection);
      setCopiedAction("cut");
      setTimeout(() => setCopiedAction(null), 1500);
    }
  }, []);

  const handlePaste = useCallback(async () => {
    const text = await navigator.clipboard.readText();
    const textarea = document.querySelector("textarea");
    if (textarea) {
      const start = textarea.selectionStart;
      const end = textarea.selectionEnd;
      const newContent = content.slice(0, start) + text + content.slice(end);
      onChange(newContent);
      setCopiedAction("paste");
      setTimeout(() => setCopiedAction(null), 1500);
    }
  }, [content, onChange]);

  const wordsLabel = t("simulation.writing.editor.words", {
    defaultValue: "words",
  });
  const cutLabel = t("simulation.writing.editor.cut", { defaultValue: "Cut" });
  const copyLabel = t("simulation.writing.editor.copy", {
    defaultValue: "Copy",
  });
  const pasteLabel = t("simulation.writing.editor.paste", {
    defaultValue: "Paste",
  });

  return (
    <div
      className={cn(
        "h-full flex flex-col bg-exam-canvas transition-all duration-500",
        isTimeWarning && "ring-2 ring-exam-timer-warning/50 animate-pulse",
      )}
    >
      {/* Minimal toolbar: cut/copy/paste on the left, optional Task overview on the right. */}
      <div className="h-10 sm:h-12 border-b border-border flex items-center justify-between px-2 sm:px-4 shrink-0 bg-card">
        <div className="flex items-center gap-0.5 sm:gap-1">
          <Button
            variant="ghost"
            size="sm"
            onClick={handleCut}
            className="h-7 sm:h-8 px-2 sm:px-2.5 text-[10px] sm:text-xs font-ui"
          >
            <Scissors className="w-3 h-3 sm:w-3.5 sm:h-3.5 mr-1 sm:mr-1.5" />
            {cutLabel}
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={handleCopy}
            className="h-7 sm:h-8 px-2 sm:px-2.5 text-[10px] sm:text-xs font-ui"
          >
            {copiedAction === "copy" ? (
              <Check className="w-3 h-3 sm:w-3.5 sm:h-3.5 mr-1 sm:mr-1.5 text-exam-success" />
            ) : (
              <Copy className="w-3 h-3 sm:w-3.5 sm:h-3.5 mr-1 sm:mr-1.5" />
            )}
            {copyLabel}
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={handlePaste}
            className="h-7 sm:h-8 px-2 sm:px-2.5 text-[10px] sm:text-xs font-ui"
          >
            <Clipboard className="w-3 h-3 sm:w-3.5 sm:h-3.5 mr-1 sm:mr-1.5" />
            {pasteLabel}
          </Button>
        </div>
        {headerRightSlot ? (
          <div className="flex items-center gap-2 shrink-0">
            {headerRightSlot}
          </div>
        ) : null}
      </div>
      <div className="border-b border-border bg-card/50 px-4 sm:px-6 py-2.5 sm:py-3 shrink-0">
        <SimulationTaskProgress
          current={progressCurrent}
          total={Math.max(progressTotal, 1)}
        />
      </div>

      {/* Writing Canvas */}
      <div className="flex-1 relative">
        <textarea
          data-writing-focus="essay"
          value={content}
          onChange={(e) => onChange(e.target.value)}
          autoComplete="off"
          spellCheck={false}
          className={cn(
            "h-full w-full resize-none p-4 font-reading text-base leading-[1.8] sm:p-8",
            "bg-transparent focus:outline-none",
            "placeholder:text-muted-foreground/50 placeholder:italic",
            "text-foreground",
          )}
          style={{
            backgroundImage:
              "repeating-linear-gradient(transparent, transparent 31px, hsl(var(--border) / 0.3) 32px)",
            backgroundSize: "100% 32px",
            paddingTop: "20px",
          }}
        />
      </div>

      {/* Word Count Footer (unchanged pill + status colors) */}
      <div className="h-10 sm:h-12 border-t border-border flex items-center justify-end px-4 sm:px-6 bg-card shrink-0">
        <div className="flex items-center gap-2 sm:gap-3">
          <div
            className={cn(
              "flex items-center gap-1 sm:gap-1.5 px-2.5 py-1 sm:px-3 sm:py-1.5 rounded-full font-ui text-[11px] sm:text-sm font-semibold transition-colors",
              status === "under" &&
                "bg-exam-timer-danger/10 text-exam-timer-danger",
              status === "good" && "bg-exam-success/10 text-exam-success",
            )}
          >
            <span className="tabular-nums">{wordCount}</span>
            <span className="text-[10px] sm:text-xs font-normal opacity-70">
              {wordsLabel}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}
